import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAttestationModal, setShowAttestationModal] = useState(false);

    // Check if user needs attestation
    const checkAttestation = useCallback((userData) => {
        if (!userData) return false;
        // Admins and moderators don't need attestation
        if (userData.role === 'admin' || userData.role === 'moderator') return false;
        // Regular users need to accept attestation
        return !userData.use_attestation_accepted;
    }, []);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('clientcheck_token');
            const savedUser = localStorage.getItem('clientcheck_user');
            
            if (token && savedUser) {
                try {
                    const response = await authAPI.getMe();
                    const userData = response.data;
                    setUser(userData);
                    localStorage.setItem('clientcheck_user', JSON.stringify(userData));
                    
                    // Check if attestation is needed
                    if (checkAttestation(userData)) {
                        setShowAttestationModal(true);
                    }
                } catch (err) {
                    localStorage.removeItem('clientcheck_token');
                    localStorage.removeItem('clientcheck_user');
                }
            }
            setLoading(false);
        };

        initAuth();
    }, [checkAttestation]);

    // Listen for attestation-required events from API interceptor
    useEffect(() => {
        const handleAttestationRequired = () => {
            if (user && checkAttestation(user)) {
                setShowAttestationModal(true);
            }
        };

        window.addEventListener('attestation-required', handleAttestationRequired);
        return () => {
            window.removeEventListener('attestation-required', handleAttestationRequired);
        };
    }, [user, checkAttestation]);

    const login = async (email, password) => {
        setError(null);
        try {
            const response = await authAPI.login({ email, password });
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('clientcheck_token', access_token);
            localStorage.setItem('clientcheck_user', JSON.stringify(userData));
            setUser(userData);
            
            // Check if attestation is needed after login
            if (checkAttestation(userData)) {
                setShowAttestationModal(true);
            }
            
            return userData;
        } catch (err) {
            const message = err.response?.data?.detail || 'Login failed';
            setError(message);
            throw new Error(message);
        }
    };

    // Direct login with token and user data (used by admin login)
    const loginWithToken = (token, userData) => {
        localStorage.setItem('clientcheck_token', token);
        localStorage.setItem('clientcheck_user', JSON.stringify(userData));
        setUser(userData);
        
        // Check if attestation is needed
        if (checkAttestation(userData)) {
            setShowAttestationModal(true);
        }
    };

    const signup = async (data) => {
        setError(null);
        try {
            const response = await authAPI.signup(data);
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('clientcheck_token', access_token);
            localStorage.setItem('clientcheck_user', JSON.stringify(userData));
            setUser(userData);
            
            // New users always need attestation
            if (checkAttestation(userData)) {
                setShowAttestationModal(true);
            }
            
            return userData;
        } catch (err) {
            const message = err.response?.data?.detail || 'Signup failed';
            setError(message);
            throw new Error(message);
        }
    };

    const logout = () => {
        localStorage.removeItem('clientcheck_token');
        localStorage.removeItem('clientcheck_user');
        setUser(null);
        setShowAttestationModal(false);
    };

    const updateUser = (updatedUser) => {
        setUser(updatedUser);
        localStorage.setItem('clientcheck_user', JSON.stringify(updatedUser));
        // Check if attestation modal should be hidden after update
        if (updatedUser.use_attestation_accepted) {
            setShowAttestationModal(false);
        }
    };

    // Handle attestation acceptance
    const handleAttestationAccept = (updatedUser) => {
        setUser(updatedUser);
        localStorage.setItem('clientcheck_user', JSON.stringify(updatedUser));
        setShowAttestationModal(false);
    };

    // Trigger attestation modal (can be called from 403 interceptor)
    const triggerAttestationModal = () => {
        if (user && checkAttestation(user)) {
            setShowAttestationModal(true);
        }
    };

    const value = {
        user,
        loading,
        error,
        login,
        loginWithToken,
        signup,
        logout,
        updateUser,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isModerator: user?.role === 'moderator' || user?.role === 'admin',
        // Attestation
        showAttestationModal,
        handleAttestationAccept,
        triggerAttestationModal,
        needsAttestation: user ? checkAttestation(user) : false
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

